import { useRef, useState } from "react";
import { getThemeDesign } from "../theme/theme.manager";

/**
 * 入力ボックスのプロパティ
 */
interface InputBoxProps {
  onSendMessage: (message: string) => void;
  onKeyUp?: (e: React.KeyboardEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled: boolean;
}

/**
 * 送信ボタンスタイル
 */
const sendButtonStyles = `
  absolute w-10 h-10 bottom-4 right-5 
  transition-transform duration-200 ease-in-out
  hover:scale-125 active:scale-200
  disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed
`;

/**
 * 送信アイコンスタイル
 */
const sendIconStyles = `
  w-7 h-7 transition-all duration-200 ease-in-out
  select-none pointer-events-none
`;

/**
 * 入力ボックスコンポーネント
 */
const InputBox: React.FC<InputBoxProps> = ({
  onSendMessage,
  onKeyUp,
  onKeyDown,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const themeDesign = getThemeDesign();

  // 入力値の変更を処理する

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  // メッセージを送信する
  const handleSend = () => {
    if (inputValue.trim() && !disabled) {
      onSendMessage(inputValue);
      setInputValue("");
    }
  };

  // キー押下する
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled) {
        buttonRef.current?.setAttribute("aria-pressed", "true");
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // キーアップする
  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (!disabled) {
        buttonRef.current?.setAttribute("aria-pressed", "false");
        if (inputValue.trim()) {
          handleSend();
        }
      }
    }
    if (onKeyUp) {
      onKeyUp(e);
    }
  };

  const isActive = !disabled && !!inputValue.trim();

  return (
    <div
      className="relative flex w-full items-center p-3 rounded-3xl"
      style={{ backgroundColor: "var(--footer-bg)" }}
    >
      <textarea
        className="pathbee-input-field pr-14 min-h-[72px] resize-none"
        placeholder={
          themeDesign.input_field_placeholder ||
          "ここに質問を入力：例「BIGストレージについて教えて」"
        }
        value={inputValue}
        onChange={handleInputChange}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        rows={3}
      />

      <button
        ref={buttonRef}
        onClick={handleSend}
        disabled={disabled || !inputValue.trim()}
        aria-pressed="false"
        aria-label="メッセージ送信"
        className={`pathbee-send-btn ${sendButtonStyles} ${
          isActive ? "opacity-100" : "opacity-30"
        }`}
        type="button"
      >
        <div
          className={`pathbee-send-icon ${sendIconStyles}`}
          style={{
            mask: "var(--img-btn-send) no-repeat center/contain",
            WebkitMask: "var(--img-btn-send) no-repeat center/contain",
            backgroundColor: isActive
              ? themeDesign.send_button_enabled_color
              : themeDesign.send_button_disabled_color,
          }}
          role="img"
          aria-label="メッセージ送信"
        />
      </button>
    </div>
  );
};

export default InputBox;
